/**
 * AudioBridge — handles WebSocket bidirectional audio streaming + recording
 *
 * Protocol (wa-calls-go):
 * - Sample rate: 16kHz
 * - Encoding: PCM16 (signed 16-bit little-endian)
 * - Channels: mono
 * - Frame size: 640 bytes (320 samples = 20ms)
 *
 * Recording: full-duplex — mixes mic (local) + playback (remote) into a
 * single MediaStream via AudioContext destination, so the .webm captures
 * both sides of the conversation.
 *
 * Supports multiple concurrent recordings keyed by callId so simultaneous
 * calls don't interfere with each other.
 */

const SAMPLE_RATE = 16000;
const FRAME_SIZE = 640;

/** Per-call recording session — isolates each call's recorder */
export interface RecorderSession {
  callId: string;
  recorder: MediaRecorder;
  chunks: Blob[];
  startedAt: string; // ISO timestamp
  startTime: number; // Date.now() for duration calc
}

/** Result returned after stopping a recording */
export interface RecordingStopResult {
  blob: Blob;
  durationSeconds: number;
  startedAt: string;
  endedAt: string;
}

export class AudioBridge {
  private ws: WebSocket | null = null;
  private audioCtx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  // Playback scheduling — `nextPlayTime` is the AudioContext timestamp where
  // the next incoming frame should start playing. Each arriving frame advances
  // this cursor by its own duration, giving gap-free sample-accurate playback.
  // If we fall behind (jitter gap / tab throttling), we snap forward to `now +
  // JITTER_BUFFER_S` so audio resumes immediately instead of playing stale queue.
  private nextPlayTime = 0;
  private readonly JITTER_BUFFER_S = 0.04; // 40ms — balance between latency and jitter tolerance
  private _muted = false;

  // Mix destination for full-duplex recording (mic + remote)
  private mixDestination: MediaStreamAudioDestinationNode | null = null;
  private micSourceForMix: MediaStreamAudioSourceNode | null = null;
  private micGainForMix: GainNode | null = null;

  // Per-call recording sessions
  private recordings = new Map<string, RecorderSession>();

  get muted() { return this._muted; }

  /** Get an AnalyserNode for audio level visualization (mic input) */
  getAnalyser(): AnalyserNode | null {
    if (!this.audioCtx || !this.micStream) return null;
    const analyser = this.audioCtx.createAnalyser();
    analyser.fftSize = 256;
    const src = this.audioCtx.createMediaStreamSource(this.micStream);
    src.connect(analyser);
    return analyser;
  }

  /** Check if a specific call (or any call) is being recorded */
  isRecording(callId?: string): boolean {
    if (callId) return this.recordings.has(callId);
    return this.recordings.size > 0;
  }

  /** Get all active recording call IDs */
  getActiveRecordingIds(): string[] {
    return Array.from(this.recordings.keys());
  }

  async connect(wsUrl: string): Promise<void> {
    this.audioCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
    this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });

    // Create a mix destination that combines mic + remote playback for recording
    this.mixDestination = this.audioCtx.createMediaStreamDestination();
    this.micSourceForMix = this.audioCtx.createMediaStreamSource(this.micStream);
    this.micGainForMix = this.audioCtx.createGain();
    this.micGainForMix.gain.value = 1.0;
    this.micSourceForMix.connect(this.micGainForMix);
    this.micGainForMix.connect(this.mixDestination);

    this.ws = new WebSocket(wsUrl);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onmessage = (event) => {
      const data = event.data as ArrayBuffer;
      if (data.byteLength < 6) return;
      this.enqueuePlayback(data);
    };

    this.ws.onerror = (err) => {
      console.error('[AudioBridge] WS error:', err);
    };

    await new Promise<void>((resolve, reject) => {
      this.ws!.onopen = () => resolve();
      this.ws!.onerror = () => reject(new Error('WebSocket connection failed'));
    });

    this.startCapture();
  }

  // ─── Recording (per-call isolated) ───

  /**
   * Start recording for a specific call.
   * Each call gets its own MediaRecorder so simultaneous calls don't conflict.
   */
  startRecording(callId: string): boolean {
    if (this.recordings.has(callId)) return true; // already recording

    const recordingStream = this.mixDestination?.stream || this.micStream;
    if (!recordingStream) return false;

    try {
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(recordingStream, { mimeType });
      const session: RecorderSession = {
        callId,
        recorder,
        chunks: [],
        startedAt: new Date().toISOString(),
        startTime: Date.now(),
      };

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) session.chunks.push(e.data);
      };

      recorder.start(1000); // chunk every 1s
      this.recordings.set(callId, session);
      console.log(`[AudioBridge] Recording started for call ${callId}`);
      return true;
    } catch (err) {
      console.warn('[AudioBridge] Recording not supported:', err);
      return false;
    }
  }

  /**
   * Stop recording for a specific call and return the blob.
   */
  async stopRecording(callId: string): Promise<RecordingStopResult | null> {
    const session = this.recordings.get(callId);
    if (!session) return null;

    const { recorder } = session;
    if (recorder.state === 'inactive') {
      this.recordings.delete(callId);
      return null;
    }

    return new Promise((resolve) => {
      recorder.onstop = () => {
        const endedAt = new Date().toISOString();
        const durationSeconds = Math.round((Date.now() - session.startTime) / 1000);
        this.recordings.delete(callId);

        if (session.chunks.length === 0) {
          console.warn(`[AudioBridge] Recording for call ${callId} ended with no chunks (call too short?)`);
          resolve(null);
          return;
        }

        const blob = new Blob(session.chunks, { type: 'audio/webm' });
        console.log(`[AudioBridge] Recording stopped for call ${callId}: ${durationSeconds}s, ${blob.size} bytes`);
        resolve({ blob, durationSeconds, startedAt: session.startedAt, endedAt });
      };

      // Force any in-flight encoder data to be flushed BEFORE we stop. Without
      // this, calls that end in under 1s (one timeslice tick) have an empty
      // chunks array because `dataavailable` never fired — the recording was
      // lost. `requestData` synchronously schedules a `dataavailable` event
      // with whatever the encoder has buffered, then `stop()` triggers a final
      // flush + the onstop callback.
      try {
        if (typeof recorder.requestData === 'function') recorder.requestData();
      } catch { /* MediaRecorder may not support — fall through to stop() */ }
      recorder.stop();
    });
  }

  /**
   * Emergency flush: synchronously collect whatever chunks we have for all
   * active recordings. Used by beforeunload to salvage data before page closes.
   * Returns blobs keyed by callId.
   */
  flushAllRecordings(): Map<string, { blob: Blob; durationSeconds: number; startedAt: string; endedAt: string }> {
    const results = new Map<string, { blob: Blob; durationSeconds: number; startedAt: string; endedAt: string }>();
    const endedAt = new Date().toISOString();

    for (const [callId, session] of this.recordings) {
      // Request final data chunk
      if (session.recorder.state === 'recording') {
        try { session.recorder.stop(); } catch { /* ignore */ }
      }

      if (session.chunks.length > 0) {
        const blob = new Blob(session.chunks, { type: 'audio/webm' });
        const durationSeconds = Math.round((Date.now() - session.startTime) / 1000);
        results.set(callId, { blob, durationSeconds, startedAt: session.startedAt, endedAt });
      }
    }

    this.recordings.clear();
    return results;
  }

  // ─── Mic capture → WebSocket ───

  private startCapture(): void {
    if (!this.audioCtx || !this.micStream) return;

    const source = this.audioCtx.createMediaStreamSource(this.micStream);
    this.scriptProcessor = this.audioCtx.createScriptProcessor(1024, 1, 1);

    let residual = new Int16Array(0);

    this.scriptProcessor.onaudioprocess = (e) => {
      if (this._muted || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;

      const input = e.inputBuffer.getChannelData(0);
      const resampled = this.audioCtx!.sampleRate === SAMPLE_RATE
        ? input
        : this.resample(input, this.audioCtx!.sampleRate, SAMPLE_RATE);

      const pcm16 = new Int16Array(resampled.length + residual.length);
      pcm16.set(residual, 0);
      for (let i = 0; i < resampled.length; i++) {
        const s = Math.max(-1, Math.min(1, resampled[i]));
        pcm16[residual.length + i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }

      const samplesPerFrame = FRAME_SIZE / 2;
      let offset = 0;
      while (offset + samplesPerFrame <= pcm16.length) {
        const frame = pcm16.slice(offset, offset + samplesPerFrame);
        this.ws!.send(frame.buffer);
        offset += samplesPerFrame;
      }
      residual = pcm16.slice(offset);
    };

    source.connect(this.scriptProcessor);
    this.scriptProcessor.connect(this.audioCtx.destination);
  }

  // ─── Playback ───

  /**
   * Schedule the next incoming PCM16 frame on the AudioContext clock.
   *
   * Uses `source.start(nextPlayTime)` so consecutive buffers are stitched
   * together with zero gap — the previous naive `source.onended`-chained
   * approach left a ~5-20ms gap between every 20ms frame, which the user
   * perceives as constant stuttering. With scheduled playback the audio is
   * seamless as long as frames arrive before the cursor passes them.
   */
  private enqueuePlayback(data: ArrayBuffer): void {
    if (!this.audioCtx) return;
    const int16 = new Int16Array(data);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7FFF);
    }

    const buffer = this.audioCtx.createBuffer(1, float32.length, SAMPLE_RATE);
    buffer.getChannelData(0).set(float32);
    const source = this.audioCtx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioCtx.destination);
    if (this.mixDestination) source.connect(this.mixDestination);

    const now = this.audioCtx.currentTime;
    // Snap forward if we fell behind (first frame, tab throttling, packet loss burst).
    if (this.nextPlayTime < now + this.JITTER_BUFFER_S) {
      this.nextPlayTime = now + this.JITTER_BUFFER_S;
    }
    source.start(this.nextPlayTime);
    this.nextPlayTime += float32.length / SAMPLE_RATE;
  }

  // ─── Resample ───

  private resample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
    const ratio = fromRate / toRate;
    const outLen = Math.floor(input.length / ratio);
    const output = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const srcIdx = i * ratio;
      const idx = Math.floor(srcIdx);
      const frac = srcIdx - idx;
      output[i] = idx + 1 < input.length
        ? input[idx] * (1 - frac) + input[idx + 1] * frac
        : input[idx];
    }
    return output;
  }

  // ─── Controls ───

  mute(): void { this._muted = true; }
  unmute(): void { this._muted = false; }

  disconnect(): void {
    // Stop all active recordings
    for (const [callId, session] of this.recordings) {
      if (session.recorder.state !== 'inactive') {
        try { session.recorder.stop(); } catch { /* ignore */ }
      }
    }
    this.recordings.clear();

    // Clean up mix destination nodes
    this.micGainForMix?.disconnect();
    this.micGainForMix = null;
    this.micSourceForMix?.disconnect();
    this.micSourceForMix = null;
    this.mixDestination = null;

    this.scriptProcessor?.disconnect();
    this.scriptProcessor = null;
    this.micStream?.getTracks().forEach(t => t.stop());
    this.micStream = null;
    this.ws?.close();
    this.ws = null;
    this.audioCtx?.close();
    this.audioCtx = null;
    this.nextPlayTime = 0;
  }
}
